import { useMemo, useState } from "react";
import { inputStyle } from "./ui.js";
import type { Member } from "../lib/types.js";

interface MemberPickerProps {
  members: Member[];
  /** Already on the court — shown as chosen, not offered again. */
  chosen: string[];
  /** Member ids you have played with recently, most recent first. */
  recent?: string[];
  onPick: (memberId: string) => void;
  label: string;
  /**
   * Offer to create somebody by the typed name — an organiser adding a child without a login.
   * Only offered when no member at all already has that exact name, and always after the
   * members who match, so picking the child who already exists is the easy thing to do.
   */
  onCreate?: (name: string) => void;
  /** What creating is, after the quoted name: `as a new child`. */
  createLabel?: string;
}

/**
 * Typeahead over the community. Recent partners and opponents come first, because in
 * practice you play with the same dozen people and typing their name every time is the
 * difference between logging a match in 20 seconds and not logging it at all.
 *
 * Before anything is typed, the recent people are a wrap of chips rather than a list: the log
 * screen has up to four of these stacked, and eight 48px rows under each was a long scroll
 * past the same names four times. Typing switches to rows, which have room for a full name.
 *
 * There is no free-text guest on purpose: every participant must be a member (requirements
 * §8.1). A guest needs an assumed rating, and an assumed rating pollutes every real rating
 * it touches. `onCreate` is not that — it makes a real member without a login, and only where
 * a screen asks for it.
 */
export function MemberPicker({
  members,
  chosen,
  recent = [],
  onPick,
  label,
  onCreate,
  createLabel = "as someone new",
}: MemberPickerProps) {
  const [query, setQuery] = useState("");

  const searching = query.trim().length > 0;

  const candidates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const available = members.filter(
      (member) => member.status === "active" && !chosen.includes(member.id),
    );

    const matching = needle
      ? available.filter((member) => member.displayName.toLowerCase().includes(needle))
      : available;

    const rank = (member: Member) => {
      const index = recent.indexOf(member.id);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };

    return [...matching]
      .sort(
        (left, right) =>
          rank(left) - rank(right) || left.displayName.localeCompare(right.displayName),
      )
      .slice(0, needle ? 6 : 8);
  }, [members, chosen, recent, query]);

  // Unsearched, only the people you actually play with. Strangers sorted alphabetically are
  // not a suggestion; for them, type.
  const suggestions = searching ? [] : candidates.filter((member) => recent.includes(member.id));

  // Against everybody, not just who is offered: a child already entered is hidden from the
  // list, and offering to create them again is how one child ends up as two.
  const typed = query.trim();
  const creatable =
    onCreate !== undefined &&
    typed.length > 0 &&
    !members.some((member) => member.displayName.toLowerCase() === typed.toLowerCase());

  const pick = (memberId: string) => {
    onPick(memberId);
    setQuery("");
  };

  return (
    <div>
      <label className="block">
        <span className="field-label">{label}</span>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Type a name"
          style={inputStyle}
        />
      </label>

      {suggestions.length > 0 ? (
        <div className="mt-2.5">
          <span className="font-mono mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-muted">
            Recent
          </span>
          <ul className="flex flex-wrap gap-2">
            {suggestions.map((member) => (
              <li key={member.id}>
                <button
                  type="button"
                  onClick={() => pick(member.id)}
                  className="border-[1.5px] px-3 text-sm font-semibold"
                  style={{
                    minHeight: 44,
                    borderColor: "var(--color-line)",
                    borderRadius: "var(--radius-control)",
                  }}
                >
                  {member.displayName}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <ul>
        {(searching ? candidates : []).map((member) => (
          <li key={member.id}>
            <button
              type="button"
              onClick={() => pick(member.id)}
              className="w-full border-b text-left"
              style={{ minHeight: 48, borderColor: "var(--color-line-muted)" }}
            >
              {member.displayName}
              {recent.includes(member.id) ? (
                <span className="micro ml-2" style={{ color: "var(--color-ink-muted)" }}>
                  recent
                </span>
              ) : null}
            </button>
          </li>
        ))}
        {creatable ? (
          <li>
            <button
              type="button"
              onClick={() => {
                onCreate?.(typed);
                setQuery("");
              }}
              className="w-full border-b text-left"
              style={{ minHeight: 48, borderColor: "var(--color-line-muted)" }}
            >
              Add &ldquo;{typed}&rdquo; {createLabel}
            </button>
          </li>
        ) : null}
        {searching && candidates.length === 0 && !creatable ? (
          <li
            className="border-b py-3 text-sm text-ink-muted"
            style={{ borderColor: "var(--color-line-muted)" }}
          >
            Nobody by that name. Everyone has to be an approved member before you can play them.
          </li>
        ) : null}
      </ul>
    </div>
  );
}
